import { useState, useEffect } from "react";
import { useParams, Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { 
  ArrowLeft, 
  Download, 
  User, 
  Briefcase, 
  Star, 
  CheckCircle, 
  MessageSquare,
  Target,
  Sparkles,
  FileText,
  Award,
  TrendingUp,
  Play,
  Video,
  Calendar,
  Mail,
  Clock,
  LayoutDashboard,
  Shield,
  UserCheck,
  AlertTriangle,
  XCircle,
  BarChart3,
  Brain,
  Users,
  Zap,
  ThumbsUp,
  ThumbsDown,
  Scale
} from "lucide-react";

const LEGAL_DISCLAIMERS: Record<string, { footer1: string; footer2: string }> = {
  NZ: {
    footer1: "CONFIDENTIAL - For authorized hiring personnel only. Contact details protected per NZ Privacy Act 2020.",
    footer2: "Compliant with Human Rights Act 1993 and Employment Relations Act 2000. © 2026 Vexis Talent Agency (LHA Holdings Ltd)"
  },
  AU: {
    footer1: "CONFIDENTIAL - For authorized hiring personnel only. Personal information handled per Australian Privacy Act 1988.",
    footer2: "Compliant with Fair Work Act 2009 and Australian anti-discrimination laws. © 2026 Vexis Talent Agency (LHA Holdings Ltd)"
  },
  EU: {
    footer1: "CONFIDENTIAL - GDPR compliant. AI-assisted assessment disclosed per EU AI Act. Data subject rights apply.",
    footer2: "Compliant with EU anti-discrimination directives and Pay Transparency Directive. © 2026 Vexis Talent Agency (LHA Holdings Ltd)"
  },
  USA: {
    footer1: "CONFIDENTIAL - For authorized hiring personnel only. Equal Opportunity Employer. AI tools used with human oversight.",
    footer2: "Compliant with Title VII, ADA, ADEA and applicable state employment laws. © 2026 Vexis Talent Agency (LHA Holdings Ltd)"
  },
  CAN: {
    footer1: "CONFIDENTIAL - For authorized hiring personnel only. Personal information protected per PIPEDA.",
    footer2: "Compliant with Canadian Human Rights Act and provincial employment standards. © 2026 Vexis Talent Agency (LHA Holdings Ltd)"
  }
};

interface Dossier {
  executiveSummary: string;
  professionalProfile: {
    headline: string;
    coreCompetencies: string[];
    experienceHighlights: string[];
    technicalStrengths: string[];
  };
  interviewPerformance: {
    standoutMoments: string[];
    communicationStyle: string;
    problemSolvingApproach: string;
  };
  verificationSummary: {
    authenticityScore: number;
    credentialsVerified: string[];
    consistencyNotes: string;
  };
  fitAnalysis: {
    roleAlignment: string;
    cultureFitIndicators: string[];
    potentialContributions: string[];
  };
  recommendedNextSteps: string[];
  marketingSnapshot: {
    elevatorPitch: string;
    keyDifferentiators: string[];
    idealFor: string;
  };
  generatedAt: string;
}

interface Score {
  roleFitScore: number;
  competenceScore: number;
  communicationScore: number;
  scenarioHandlingScore: number;
  overallScore: number;
  strengths: string[];
  gaps: string[];
  recommendation: string;
  credentialBoost: number;
  verifiedCredentialsCount: number;
  totalCredentialsCount: number;
}

interface Verification {
  timelineConsistency: number;
  skillDepthScore: number;
  verbalWrittenMatch: number;
  aiResumeRisk: string;
  overallConfidence: number;
  flags: string[];
  notes: string;
}

interface VideoResponse {
  questionId: string;
  question: string;
  videoUrl?: string;
  responseId?: string;
}

interface DossierData {
  candidate: {
    id: number;
    fullName: string;
    email: string;
  };
  role: {
    id: number;
    title: string;
    legalRegion?: string;
  };
  dossier?: Dossier | null;
  score?: Score;
  verification?: Verification;
  interviewVideos?: VideoResponse[];
  interviewSessionId?: string;
  humanVerified?: boolean;
  identityVerified?: boolean;
  rightToWorkVerified?: boolean;
  authenticityVerified?: boolean;
  pipelineStage?: string;
}

export default function CandidateDossier() {
  const params = useParams<{ applicationId: string }>();
  const [data, setData] = useState<DossierData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [videoUrls, setVideoUrls] = useState<Record<string, string>>({});
  
  const agentId = localStorage.getItem("agentId");
  const isAgent = !!agentId;

  useEffect(() => {
    fetchDossier();
  }, [params.applicationId]);

  const fetchDossier = async () => {
    try {
      const res = await apiRequest("GET", `/api/agent/applications/${params.applicationId}/dossier`);
      if (!res.ok) throw new Error("Failed to fetch dossier");
      const result = await res.json();
      setData(result);
      
      if (isAgent && result.interviewId && result.interviewVideos) {
        fetchVideoUrls(params.applicationId as string, result.interviewVideos);
      }
    } catch (error) {
      toast.error("Failed to generate candidate dossier");
    } finally {
      setLoading(false);
    }
  };

  const fetchVideoUrls = async (applicationId: string, videos: VideoResponse[]) => {
    const urls: Record<string, string> = {};
    for (const video of videos) {
      try {
        const res = await apiRequest("GET", `/api/agent/applications/${applicationId}/video/${video.responseId || video.questionId}`);
        if (res.ok) {
          const data = await res.json();
          urls[video.responseId || video.questionId] = data.url;
        }
      } catch (error) {
        console.error(`Failed to fetch video URL for ${video.responseId || video.questionId}`);
      }
    }
    setVideoUrls(urls);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getRecommendationBadge = (recommendation: string) => {
    switch (recommendation) {
      case "proceed":
        return <Badge className="bg-green-500 text-white"><ThumbsUp className="w-3 h-3 mr-1" />Proceed to Hire</Badge>;
      case "hold":
        return <Badge className="bg-yellow-500 text-white"><Clock className="w-3 h-3 mr-1" />Hold for Review</Badge>;
      case "decline":
        return <Badge className="bg-red-500 text-white"><ThumbsDown className="w-3 h-3 mr-1" />Not Recommended</Badge>;
      default:
        return <Badge variant="secondary">Pending Review</Badge>;
    }
  };

  const getRiskBadge = (risk: string) => {
    switch (risk) {
      case "low":
        return <Badge className="bg-green-500/10 text-green-600 border-green-500/30">Low Risk</Badge>;
      case "medium":
        return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">Medium Risk</Badge>;
      case "high":
        return <Badge className="bg-red-500/10 text-red-600 border-red-500/30">High Risk</Badge>;
      default:
        return <Badge variant="secondary">Not Assessed</Badge>;
    }
  };

  const exportDossier = () => {
    if (!data) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 20;
    
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("VEXIS", 20, 25);
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Executive Talent Dossier", 20, 35);
    
    if (data.humanVerified) {
      doc.setFillColor(34, 197, 94);
      doc.roundedRect(pageWidth - 55, 10, 45, 14, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.text("HUMAN VERIFIED", pageWidth - 52, 19);
    }
    
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(200, 200, 200);
    doc.text(`Report ID: ${params.applicationId} | Generated: ${data.dossier?.generatedAt ? new Date(data.dossier.generatedAt).toLocaleDateString('en-NZ') : 'Pending'}`, pageWidth - 55, 35);
    
    yPos = 55;
    
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(data.candidate.fullName, 20, yPos);
    yPos += 7;
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139);
    doc.text(`Candidate for: ${data.role.title}`, 20, yPos);
    yPos += 12;
    
    doc.setDrawColor(226, 232, 240);
    doc.line(20, yPos, pageWidth - 20, yPos);
    yPos += 10;
    
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(20, yPos - 5, pageWidth - 40, 45, 3, 3, 'F');
    
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("EXECUTIVE SCORECARD", 25, yPos + 3);
    
    const score = data.score;
    if (score) {
      const scoreY = yPos + 12;
      const colWidth = (pageWidth - 60) / 5;
      
      const scores = [
        { label: "Overall", value: score.overallScore },
        { label: "Role Fit", value: score.roleFitScore },
        { label: "Competence", value: score.competenceScore },
        { label: "Communication", value: score.communicationScore },
        { label: "Scenarios", value: score.scenarioHandlingScore }
      ];
      
      scores.forEach((s, i) => {
        const x = 25 + (i * colWidth);
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        if (s.value >= 80) doc.setTextColor(34, 197, 94);
        else if (s.value >= 60) doc.setTextColor(234, 179, 8);
        else doc.setTextColor(239, 68, 68);
        doc.text(`${s.value}`, x, scoreY + 5);
        
        const barWidth = 25;
        const barX = x;
        const barY = scoreY + 8;
        doc.setFillColor(226, 232, 240);
        doc.roundedRect(barX, barY, barWidth, 3, 1, 1, 'F');
        if (s.value >= 80) doc.setFillColor(34, 197, 94);
        else if (s.value >= 60) doc.setFillColor(234, 179, 8);
        else doc.setFillColor(239, 68, 68);
        doc.roundedRect(barX, barY, (barWidth * s.value) / 100, 3, 1, 1, 'F');
        
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 116, 139);
        doc.text(s.label, x, scoreY + 18);
      });
      
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      const recText = score.recommendation === "proceed" ? "RECOMMENDED" : score.recommendation === "hold" ? "HOLD FOR REVIEW" : "NOT RECOMMENDED";
      if (score.recommendation === "proceed") doc.setTextColor(34, 197, 94);
      else if (score.recommendation === "hold") doc.setTextColor(234, 179, 8);
      else doc.setTextColor(239, 68, 68);
      doc.text(recText, pageWidth - 55, yPos + 3);
    }
    
    yPos += 50;
    
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("EXECUTIVE SUMMARY", 20, yPos);
    yPos += 6;
    
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(51, 65, 85);
    const summaryLines = doc.splitTextToSize(data.dossier?.executiveSummary ?? "Assessment pending — results will appear here once the candidate completes their interview.", pageWidth - 40);
    doc.text(summaryLines, 20, yPos);
    yPos += (summaryLines.length * 4) + 10;
    
    if (yPos > pageHeight - 60) { doc.addPage(); yPos = 20; }
    
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("VERIFICATION STATUS", 20, yPos);
    yPos += 6;
    
    const verificationItems = [
      { label: "Identity Verified", status: data.identityVerified },
      { label: "Right to Work", status: data.rightToWorkVerified },
      { label: "Credentials Authentic", status: data.authenticityVerified },
      { label: "Human Reviewed", status: data.humanVerified }
    ];
    
    verificationItems.forEach((item, i) => {
      const x = 20 + (i % 2) * 90;
      const y = yPos + Math.floor(i / 2) * 6;
      doc.setFontSize(8);
      if (item.status) {
        doc.setTextColor(34, 197, 94);
        doc.text("✓ " + item.label, x, y);
      } else {
        doc.setTextColor(148, 163, 184);
        doc.text("○ " + item.label, x, y);
      }
    });
    yPos += 18;
    
    if (data.verification) {
      doc.setFontSize(8);
      doc.setTextColor(51, 65, 85);
      doc.text(`AI Resume Risk: ${data.verification.aiResumeRisk?.toUpperCase() || 'N/A'} | Confidence: ${data.verification.overallConfidence}%`, 20, yPos);
      yPos += 10;
    }
    
    if (yPos > pageHeight - 80) { doc.addPage(); yPos = 20; }
    
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("KEY STRENGTHS", 20, yPos);
    yPos += 6;
    
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(51, 65, 85);
    
    const strengths = score?.strengths || [];
    strengths.slice(0, 4).forEach((s) => {
      const lines = doc.splitTextToSize(`• ${s}`, pageWidth - 45);
      doc.text(lines, 25, yPos);
      yPos += lines.length * 4 + 2;
    });
    yPos += 8;
    
    if (score?.gaps && score.gaps.length > 0) {
      if (yPos > pageHeight - 60) { doc.addPage(); yPos = 20; }
      
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("AREAS FOR CONSIDERATION", 20, yPos);
      yPos += 6;
      
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      
      score.gaps.slice(0, 3).forEach((g) => {
        const lines = doc.splitTextToSize(`• ${g}`, pageWidth - 45);
        doc.text(lines, 25, yPos);
        yPos += lines.length * 4 + 2;
      });
    }
    
    const footerY = pageHeight - 20;
    doc.setDrawColor(226, 232, 240);
    doc.line(20, footerY - 5, pageWidth - 20, footerY - 5);
    
    const legalRegion = data.role.legalRegion || "NZ";
    const disclaimers = LEGAL_DISCLAIMERS[legalRegion] || LEGAL_DISCLAIMERS.NZ;
    
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(disclaimers.footer1, 20, footerY);
    doc.text(disclaimers.footer2, 20, footerY + 5);
    
    doc.save(`VEXIS-Executive-Dossier-${data.candidate.fullName.replace(/\s+/g, '-')}.pdf`);
    toast.success("Executive dossier exported as PDF");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-xl font-semibold">Generating Executive Dossier...</p>
          <p className="text-muted-foreground mt-2">Compiling interview metrics and verification data</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">Failed to load dossier</p>
          <Link href="/agent/dashboard">
            <Button className="mt-4">Back to Dashboard</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const { candidate, role, dossier, score, verification, interviewVideos } = data;
  const PENDING_MSG = "Assessment pending — results will appear here once the candidate completes their interview.";

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link href="/agent/dashboard">
              <Button variant="ghost" size="sm" data-testid="button-back">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded" />
              <span className="text-xl font-bold tracking-wider">VEXIS</span>
              <Badge variant="outline" className="ml-2 text-xs">Executive Dossier</Badge>
            </div>
          </div>
          <div className="flex gap-3">
            {isAgent && (
              <Link href="/agent/dashboard">
                <Button variant="outline" size="sm" data-testid="button-agent-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Agent Dashboard
                </Button>
              </Link>
            )}
            <Button onClick={exportDossier} data-testid="button-export">
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>
      </nav>

      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">{candidate.fullName}</h1>
                <p className="text-slate-300 flex items-center gap-2 mt-1">
                  <Briefcase className="w-4 h-4" />
                  Candidate for {role.title}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              {data.humanVerified && (
                <Badge className="bg-green-500 text-white border-0 text-sm px-4 py-1.5">
                  <UserCheck className="w-4 h-4 mr-2" />
                  Human Verified
                </Badge>
              )}
              <p className="text-xs text-slate-400">
                Report ID: {params.applicationId} | {dossier?.generatedAt ? new Date(dossier.generatedAt).toLocaleDateString('en-NZ') : 'Pending'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {score && (
        <div className="bg-slate-50 border-b">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Executive Scorecard
              </h2>
              {getRecommendationBadge(score.recommendation)}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="p-4 text-center border-2 border-primary/20 bg-white">
                <p className={`text-4xl font-bold ${getScoreColor(score.overallScore)}`}>{score.overallScore}</p>
                <p className="text-xs text-muted-foreground mt-1">Overall Score</p>
                <div className="h-1.5 mt-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${getScoreBgColor(score.overallScore)}`} style={{ width: `${score.overallScore}%` }} />
                </div>
              </Card>
              
              <Card className="p-4 text-center bg-white">
                <p className={`text-3xl font-bold ${getScoreColor(score.roleFitScore)}`}>{score.roleFitScore}</p>
                <p className="text-xs text-muted-foreground mt-1">Role Fit</p>
                <div className="h-1.5 mt-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${getScoreBgColor(score.roleFitScore)}`} style={{ width: `${score.roleFitScore}%` }} />
                </div>
              </Card>
              
              <Card className="p-4 text-center bg-white">
                <p className={`text-3xl font-bold ${getScoreColor(score.competenceScore)}`}>{score.competenceScore}</p>
                <p className="text-xs text-muted-foreground mt-1">Competence</p>
                <div className="h-1.5 mt-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${getScoreBgColor(score.competenceScore)}`} style={{ width: `${score.competenceScore}%` }} />
                </div>
              </Card>
              
              <Card className="p-4 text-center bg-white">
                <p className={`text-3xl font-bold ${getScoreColor(score.communicationScore)}`}>{score.communicationScore}</p>
                <p className="text-xs text-muted-foreground mt-1">Communication</p>
                <div className="h-1.5 mt-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${getScoreBgColor(score.communicationScore)}`} style={{ width: `${score.communicationScore}%` }} />
                </div>
              </Card>
              
              <Card className="p-4 text-center bg-white">
                <p className={`text-3xl font-bold ${getScoreColor(score.scenarioHandlingScore)}`}>{score.scenarioHandlingScore}</p>
                <p className="text-xs text-muted-foreground mt-1">Scenario Handling</p>
                <div className="h-1.5 mt-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${getScoreBgColor(score.scenarioHandlingScore)}`} style={{ width: `${score.scenarioHandlingScore}%` }} />
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6 border-l-4 border-l-primary">
              <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Executive Summary
              </h2>
              <p className="text-base leading-relaxed">{dossier?.executiveSummary ?? PENDING_MSG}</p>
            </Card>

            {score && (
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-6">
                  <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <ThumbsUp className="w-5 h-5 text-green-500" />
                    Key Strengths
                  </h2>
                  <ul className="space-y-2">
                    {score.strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </Card>

                <Card className="p-6">
                  <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Areas for Consideration
                  </h2>
                  <ul className="space-y-2">
                    {score.gaps.length > 0 ? score.gaps.map((g, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <XCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span>{g}</span>
                      </li>
                    )) : (
                      <li className="text-sm text-muted-foreground">No significant gaps identified</li>
                    )}
                  </ul>
                </Card>
              </div>
            )}

            <Card className="p-6">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Verification Status
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-3 rounded-lg text-center ${data.identityVerified ? 'bg-green-500/10 border border-green-500/30' : 'bg-muted'}`}>
                  {data.identityVerified ? (
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-1" />
                  ) : (
                    <XCircle className="w-6 h-6 text-muted-foreground mx-auto mb-1" />
                  )}
                  <p className="text-xs font-medium">Identity</p>
                </div>
                
                <div className={`p-3 rounded-lg text-center ${data.rightToWorkVerified ? 'bg-green-500/10 border border-green-500/30' : 'bg-muted'}`}>
                  {data.rightToWorkVerified ? (
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-1" />
                  ) : (
                    <XCircle className="w-6 h-6 text-muted-foreground mx-auto mb-1" />
                  )}
                  <p className="text-xs font-medium">Right to Work</p>
                </div>
                
                <div className={`p-3 rounded-lg text-center ${data.authenticityVerified ? 'bg-green-500/10 border border-green-500/30' : 'bg-muted'}`}>
                  {data.authenticityVerified ? (
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-1" />
                  ) : (
                    <XCircle className="w-6 h-6 text-muted-foreground mx-auto mb-1" />
                  )}
                  <p className="text-xs font-medium">Authenticity</p>
                </div>
                
                <div className={`p-3 rounded-lg text-center ${data.humanVerified ? 'bg-green-500/10 border border-green-500/30' : 'bg-muted'}`}>
                  {data.humanVerified ? (
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-1" />
                  ) : (
                    <XCircle className="w-6 h-6 text-muted-foreground mx-auto mb-1" />
                  )}
                  <p className="text-xs font-medium">Human Review</p>
                </div>
              </div>

              {verification && (
                <div className="grid md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Timeline Consistency</p>
                    <div className="flex items-center gap-2">
                      <Progress value={verification.timelineConsistency} className="h-2 flex-1" />
                      <span className="text-sm font-bold">{verification.timelineConsistency}%</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Skill Depth</p>
                    <div className="flex items-center gap-2">
                      <Progress value={verification.skillDepthScore} className="h-2 flex-1" />
                      <span className="text-sm font-bold">{verification.skillDepthScore}%</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">AI Resume Risk</p>
                    {getRiskBadge(verification.aiResumeRisk)}
                  </div>
                </div>
              )}
            </Card>

            <Card className="p-6">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Brain className="w-5 h-5 text-secondary" />
                Professional Profile
              </h2>
              {dossier?.professionalProfile ? (
                <>
                  <p className="text-base font-medium text-primary mb-4">{dossier.professionalProfile.headline}</p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-semibold text-muted-foreground mb-2">Core Competencies</h3>
                      <div className="flex flex-wrap gap-2">
                        {(dossier.professionalProfile.coreCompetencies ?? []).map((c, i) => (
                          <Badge key={i} variant="secondary">{c}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-muted-foreground mb-2">Technical Strengths</h3>
                      <div className="flex flex-wrap gap-2">
                        {(dossier.professionalProfile.technicalStrengths ?? []).map((s, i) => (
                          <Badge key={i} variant="outline" className="border-primary text-primary">{s}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground text-sm">{PENDING_MSG}</p>
              )}
            </Card>

            <Card className="p-6">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-accent" />
                Role Fit Analysis
              </h2>
              {dossier?.fitAnalysis ? (
                <>
                  <p className="mb-4">{dossier.fitAnalysis.roleAlignment}</p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-semibold text-muted-foreground mb-2">Culture Fit Indicators</h3>
                      <ul className="space-y-1">
                        {(dossier.fitAnalysis.cultureFitIndicators ?? []).map((c, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <Users className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-muted-foreground mb-2">Potential Contributions</h3>
                      <ul className="space-y-1">
                        {(dossier.fitAnalysis.potentialContributions ?? []).map((c, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <Zap className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground text-sm">{PENDING_MSG}</p>
              )}
            </Card>
          </div>

          <div className="space-y-6">
            {isAgent ? (
              <Card className="p-6 sticky top-20">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Video className="w-5 h-5 text-primary" />
                  Interview Recording
                </h2>
                
                {interviewVideos && interviewVideos.length > 0 ? (
                  <div className="space-y-4">
                    <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
                      {videoUrls[interviewVideos[activeVideoIndex]?.questionId] ? (
                        <video
                          key={activeVideoIndex}
                          src={videoUrls[interviewVideos[activeVideoIndex].questionId]}
                          controls
                          className="w-full h-full object-cover"
                          data-testid="video-player"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center text-white/70">
                            <Play className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">Loading...</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-medium">
                        Q{activeVideoIndex + 1}/{interviewVideos.length}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {interviewVideos[activeVideoIndex]?.question}
                      </p>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      {interviewVideos.map((_, index) => (
                        <Button
                          key={index}
                          size="sm"
                          variant={index === activeVideoIndex ? "default" : "outline"}
                          onClick={() => setActiveVideoIndex(index)}
                          className="w-8 h-8 p-0"
                          data-testid={`video-button-${index}`}
                        >
                          {index + 1}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                    <div className="text-center text-muted-foreground">
                      <Video className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No recordings available</p>
                    </div>
                  </div>
                )}
              </Card>
            ) : (
              <Card className="p-6 sticky top-20 border-green-500/30 bg-green-500/5">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-500" />
                  Interview Verified
                </h2>
                <div className="text-center py-4">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                  <p className="font-semibold mb-2">Agent-Verified Interview</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    This candidate's video interview has been reviewed and authenticated by our recruitment agents.
                  </p>
                  <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                    <Award className="w-3 h-3 mr-1" />
                    Verified Performance
                  </Badge>
                </div>
              </Card>
            )}

            <Card className="p-6">
              <h2 className="text-lg font-bold mb-4">Recommended Actions</h2>
              {dossier?.recommendedNextSteps?.length ? (
                <ol className="space-y-3">
                  {dossier.recommendedNextSteps.map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                        {i + 1}
                      </span>
                      <span className="text-sm pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-muted-foreground text-sm">{PENDING_MSG}</p>
              )}
            </Card>

            {score && score.verifiedCredentialsCount > 0 && (
              <Card className="p-6">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  Verified Credentials
                </h2>
                <div className="text-center p-4 bg-primary/5 rounded-lg">
                  <p className="text-3xl font-bold text-primary">{score.verifiedCredentialsCount}</p>
                  <p className="text-sm text-muted-foreground">of {score.totalCredentialsCount} verified</p>
                  {score.credentialBoost > 0 && (
                    <Badge className="mt-2 bg-primary/10 text-primary">
                      +{score.credentialBoost}% Score Boost
                    </Badge>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Scale className="w-4 h-4" />
                <span>NZ Privacy Act 2020 Compliant</span>
              </div>
              <span>•</span>
              <span>Human Rights Act 1993</span>
              <span>•</span>
              <span>Employment Relations Act 2000</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gradient-to-br from-primary to-secondary rounded" />
              <span>VEXIS Agent-Verified Platform</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
